import React, { useEffect, useMemo, useState } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
  useLocation
} from "react-router-dom";
import { toast } from "sonner";
import "./App.css";
import { fetchMe, fetchProfile, logout } from "./api";
import { AuthUser } from "./types";
import { TABLET_QUERY, useMediaQuery } from "./hooks/use-media-query";
import LoginScreen from "./components/LoginScreen";
import AppSidebar, {
  DesktopHeader,
  MobileBottomNav,
  MobileHeader
} from "./components/AppSidebar";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar
} from "./components/ui/sidebar";
import { Toaster } from "./components/ui/sonner";
import RequireRole from "./components/RequireRole";
import RequireManager from "./components/RequireManager";
import {
  useBackClosesOnGesture,
  useHardwareBackNavigation
} from "./lib/navigation";
import { usePreferences } from "./lib/preferences";
import Dashboard from "./pages/Dashboard";
import MyRequests from "./pages/MyRequests";
import NewRequest from "./pages/requests/NewRequest";
import OtherReimbursements from "./pages/OtherReimbursements";
import NewOtherRequest from "./pages/requests/NewOtherRequest";
import MyApprovals from "./pages/MyApprovals";
import ApproverQueue from "./pages/ApproverQueue";
import UserManagement from "./pages/admin/UserManagement";
import Policies from "./pages/admin/Policies";
import AuditLogs from "./pages/admin/AuditLogs";

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const { dark } = usePreferences();

  // Auth + initial load. Stays "checking" until BOTH calls settle - fetchMe
  // alone returns only {name, email}, with roles/isManager (needed by
  // RequireRole/RequireManager below) only arriving via fetchProfile a
  // moment later. Flipping checking=false after fetchMe alone let a direct
  // load/refresh of an admin-only route render with user.roles still
  // undefined, so RequireRole's check failed and incorrectly bounced an
  // authorized Admin back to "/" before their real roles ever loaded.
  useEffect(() => {
    fetchMe()
      .then((u) => {
        if (!u) {
          setUser(null);
          setChecking(false);
          return;
        }
        // Enrich with Graph details + admin-portal roles before rendering
        // any route - a profile-fetch failure still unblocks rendering
        // (degrades to no roles/isManager, same as before this fix), rather
        // than leaving the app stuck on the loading screen forever.
        fetchProfile()
          .then((p) => setUser({ ...u, ...p }))
          .catch(() => setUser(u))
          .finally(() => setChecking(false));
      })
      .catch(() => setChecking(false));
  }, []);

  // Full reload (not just clearing local state) is deliberate - it's the
  // simplest way to guarantee every page's in-memory state is gone after
  // logout. A brief delay lets the toast actually be seen before the reload
  // wipes it, rather than firing and vanishing in the same instant.
  const handleLogout = () => {
    logout()
      .then(() => toast.success("Signed out"))
      .catch(() => toast.error("Could not sign out - please try again."))
      .finally(() => setTimeout(() => window.location.reload(), 600));
  };

  // A data router (createBrowserRouter/RouterProvider), not the plain
  // BrowserRouter this app used before, specifically so useBlocker is
  // available (it only works with a data router - see NewRequest.tsx's
  // unsaved-bills exit guard, the reason for this migration).
  // createRoutesFromElements lets the route tree keep the exact same JSX
  // shape it had under <Routes>/<Route>, just nested as children of one
  // parent/layout route whose element is AppShell (which now renders
  // <Outlet /> where <Routes> used to sit) instead of being a sibling
  // inside it. Memoized (not rebuilt every render) because RouterProvider
  // expects a STABLE router reference - a new router object identity on
  // every render would remount the whole route tree on every keystroke
  // anywhere in the app, not just re-render it. Must run BEFORE the
  // checking/no-user early returns below (Rules of Hooks: every hook runs
  // on every render, unconditionally) - null while there's no signed-in
  // user yet, since AppShell/the routes need a real user to render.
  const router = useMemo(
    () =>
      user
        ? createBrowserRouter(
            createRoutesFromElements(
              <Route
                element={
                  <AppShell user={user} onLogout={handleLogout} />
                }
              >
                <Route path="/" element={<Dashboard user={user} />} />
                <Route path="/my-requests" element={<MyRequests />} />
                <Route path="/my-requests/new" element={<NewRequest />} />
                <Route path="/other-reimbursements" element={<OtherReimbursements />} />
                <Route path="/other-reimbursements/new" element={<NewOtherRequest />} />
                <Route
                  path="/my-approvals"
                  element={
                    <RequireManager user={user}>
                      <MyApprovals />
                    </RequireManager>
                  }
                />
                <Route
                  path="/approver-queue"
                  element={
                    <RequireRole user={user} role="Finance">
                      <ApproverQueue />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <RequireRole user={user} role="Admin">
                      <UserManagement />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/policies"
                  element={
                    <RequireRole user={user} role="Admin">
                      <Policies />
                    </RequireRole>
                  }
                />
                <Route
                  path="/admin/audit-logs"
                  element={
                    <RequireRole user={user} role="Admin">
                      <AuditLogs />
                    </RequireRole>
                  }
                />
              </Route>
            )
          )
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  if (checking) return null;
  if (!user || !router)
    return (
      <>
        <Toaster dark={dark} />
        <LoginScreen />
      </>
    );

  return (
    <>
      <Toaster dark={dark} />
      <RouterProvider router={router} />
    </>
  );
}

/** Responsive shell around every route.
 *  - Phone (<768px): sticky MobileHeader on top, MobileBottomNav tabs at
 *    the bottom; the sidebar only appears as a drawer from the "More" tab.
 *  - Tablet (768–1151px): the sidebar starts collapsed to its icon rail so
 *    list screens get the width; the header trigger still expands it. See
 *    use-media-query.ts's TABLET_QUERY comment for why this threshold sits
 *    below a common laptop's actual content width, not at Tailwind's xl.
 *  - Desktop (≥1152px): expanded sidebar by default. Collapsing it via the
 *    header trigger only lasts for the current session - deliberately NOT
 *    persisted to localStorage (an earlier version did, and a refresh
 *    coming back collapsed read as a bug, not a remembered preference), so
 *    every fresh load always starts expanded again.
 *  h-dvh (not h-screen) so mobile browsers' collapsing URL bar doesn't push
 *  the bottom nav off-screen. */
function AppShell({
  user,
  onLogout
}: {
  user: AuthUser;
  onLogout: () => void;
}) {
  const location = useLocation();
  const isTablet = useMediaQuery(TABLET_QUERY);
  // Phone-only: makes the hardware back button / edge-swipe gesture go to
  // this route's logical parent (same as the on-screen back arrow) instead
  // of replaying raw browser history - see lib/navigation.ts's docstring.
  useHardwareBackNavigation();
  // Both session-only state, same as tabletOpen below - a desktop collapse
  // is a within-session convenience, not a saved preference (see this
  // function's docstring).
  const [desktopOpen, setDesktopOpen] = useState(true);
  // Tablet has its own session-only state - expanding the rail on a
  // tablet shouldn't overwrite the desktop preference, and rotating or
  // resizing back into tablet width collapses it again.
  const [tabletOpen, setTabletOpen] = useState(false);
  useEffect(() => {
    if (isTablet) setTabletOpen(false);
  }, [isTablet]);

  const sidebarOpen = isTablet ? tabletOpen : desktopOpen;
  const setSidebarOpen = (open: boolean) => {
    if (isTablet) {
      setTabletOpen(open);
    } else {
      setDesktopOpen(open);
    }
  };

  // The request wizard is a focused, multi-step task with its own sticky
  // Back/Next bar at the bottom on phones - the tab bar is hidden there so
  // the two don't stack.
  const hideBottomNav =
    location.pathname.startsWith("/my-requests/new") ||
    location.pathname.startsWith("/other-reimbursements/new");

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      className="h-dvh overflow-hidden"
    >
      <MobileDrawerBackGuard />
      <AppSidebar user={user} />
      <SidebarInset className="min-w-0 overflow-hidden">
        <MobileHeader user={user} onLogout={onLogout} />
        <DesktopHeader user={user} onLogout={onLogout} />
        {/* SidebarInset already renders a <main> - this is a plain div,
           not a second <main>, to avoid nesting landmark elements. Bottom
           padding on phones clears the fixed MobileBottomNav (4rem + the
           device's safe-area inset). */}
        <div
          className={
            hideBottomNav
              ? "flex-1 overflow-y-auto overscroll-contain"
              : "flex-1 overflow-y-auto overscroll-contain pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0"
          }
        >
          {/* The actual route elements are now defined in App's own router
             (createRoutesFromElements) as this layout route's children,
             since a data router needs its route tree defined up front -
             AppShell (this component) is that parent route's element, and
             Outlet renders whichever child route matched. */}
          <Outlet />
        </div>
        {!hideBottomNav && <MobileBottomNav user={user} />}
      </SidebarInset>
    </SidebarProvider>
  );
}

/** Makes a phone back gesture close the mobile sidebar drawer ("More" tab)
 *  in place, instead of falling through to useHardwareBackNavigation's own
 *  page-level "go to parent" handling underneath - the same "dismiss the
 *  thing on top first" order a native app's back button follows. Rendered
 *  as its own component (not inlined in AppShell) because useSidebar()
 *  needs to run INSIDE the SidebarProvider AppShell itself renders - a
 *  parent component can't consume a context it creates. No markup of its
 *  own (returns null); it exists purely for this hook's effects. */
function MobileDrawerBackGuard() {
  const { openMobile, setOpenMobile } = useSidebar();
  useBackClosesOnGesture(openMobile, () => setOpenMobile(false));
  return null;
}
